import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Search } from 'lucide-react';

export default function CreateChannelModal() {
    const { showCreateChannel, setShowCreateChannel, createChannel, users, currentUser, settings, hasPermission } = useApp();
    const [name, setName] = useState('');
    const [allowInvites, setAllowInvites] = useState(true);
    const [encryptionMode, setEncryptionMode] = useState<'optional' | 'forced' | 'disabled'>('optional');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const isAdmin = hasPermission('admin:full') || hasPermission('super:admin');

    const filteredUsers = users.filter(u =>
        (u.id !== currentUser?.id || isAdmin) && // Only show self if admin
        (u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleClose = () => {
        setShowCreateChannel(false);
        setName('');
        setAllowInvites(true);
        setEncryptionMode('optional');
        setSelectedUsers([]);
        setSearchQuery('');
        setError('');
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        if (showCreateChannel) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showCreateChannel]);

    if (!showCreateChannel) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        setIsSubmitting(true);
        try {
            setError('');
            await createChannel(name, allowInvites, selectedUsers, encryptionMode);
            handleClose();
        } catch (err: any) {
            console.error('Failed to create channel:', err);
            setError(err.message || 'Failed to create group');
        }
        setIsSubmitting(false);
    };

    const toggleUser = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal" style={{ display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <h2 className="modal-title">Create a group</h2>
                    <button className="btn btn-ghost btn-icon" onClick={handleClose}>✕</button>
                </header>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    {error && (
                        <div className="auth-error" style={{ margin: 'var(--spacing-md) var(--spacing-md) 0' }}>
                            {error}
                        </div>
                    )}
                    <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                        <div className="form-group">
                            <label className="form-label">Group name</label>
                            <input
                                type="text"
                                className="input"
                                value={name}
                                onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                                placeholder="e.g. marketing"
                                autoFocus
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Invite Members</label>
                            <div className="modal-search-wrapper" style={{ marginBottom: 'var(--spacing-sm)' }}>
                                <Search size={16} className="modal-search-icon" />
                                <input
                                    className="modal-search-input"
                                    placeholder="Search users..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="modal-list-container" style={{ maxHeight: '200px' }}>
                                {filteredUsers.map(user => {
                                    const isSelected = selectedUsers.includes(user.id);
                                    return (
                                        <div
                                            key={user.id}
                                            onClick={() => toggleUser(user.id)}
                                            className={`user-list-item ${isSelected ? 'active' : ''}`}
                                        >
                                            <div className="avatar avatar-sm">
                                                {user.name[0].toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                                                    {user.name} {user.id === currentUser?.id && '(You)'}
                                                </div>
                                            </div>
                                            {isSelected && <span style={{ color: 'var(--accent-primary)' }}>✓</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={allowInvites}
                                    onChange={(e) => setAllowInvites(e.target.checked)}
                                />
                                <span>Allow members to invite others</span>
                            </label>
                        </div>

                        {/* Only show encryption picker if admin hasn't globally forced E2EE */}
                        {settings?.encryption_policy?.channel_mode !== 'forced' && (
                            <div className="form-group">
                                <label className="form-label">Encryption</label>
                                <select
                                    className="input"
                                    value={encryptionMode}
                                    onChange={(e) => setEncryptionMode(e.target.value as any)}
                                >
                                    <option value="optional">Optional — members choose per message</option>
                                    <option value="forced">Forced — all messages must be encrypted</option>
                                    {isAdmin && <option value="disabled">Disabled — no encryption allowed</option>}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer" style={{ flexShrink: 0 }}>
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!name.trim() || isSubmitting}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Group'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
